"use strict";
/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = __importStar(require("react"));
var isEqual_1 = __importDefault(require("lodash/isEqual"));
var omit_1 = __importDefault(require("lodash/omit"));
var get_1 = __importDefault(require("lodash/get"));
var core_1 = require("@jsonforms/core");
var react_redux_1 = require("react-redux");
var initialCoreState = {
    data: {},
    schema: {},
    uischema: undefined,
    errors: [],
    validator: function () { return true; },
    ajv: undefined,
    refParserOptions: undefined
};
exports.JsonFormsContext = react_1.default.createContext({
    core: initialCoreState,
    renderers: []
});
exports.JsonFormsStateProvider = function (_a) {
    var children = _a.children, initState = _a.initState;
    var _b = react_1.useReducer(core_1.coreReducer, initState.core), core = _b[0], dispatch = _b[1];
    var renderers = react_1.useReducer(core_1.rendererReducer, initState.renderers)[0];
    var cells = react_1.useReducer(core_1.cellReducer, initState.cells)[0];
    var _c = initState.core, data = _c.data, schema = _c.schema, uischema = _c.uischema, ajv = _c.ajv, refParserOptions = _c.refParserOptions;
    react_1.useEffect(function () {
        dispatch(core_1.Actions.init(data, schema, uischema, { ajv: ajv, refParserOptions: refParserOptions }));
    }, [data, schema, uischema, ajv, refParserOptions]);
    return (react_1.default.createElement(exports.JsonFormsContext.Provider, { value: {
            core: core,
            renderers: renderers,
            cells: cells,
            // only core dispatch available
            dispatch: dispatch
        } }, children));
};
exports.useJsonForms = function () {
    return react_1.useContext(exports.JsonFormsContext);
};
var JsonFormsReduxProvider = function (_a) {
    var children = _a.children, dispatch = _a.dispatch, other = __rest(_a, ["children", "dispatch"]);
    return (react_1.default.createElement(exports.JsonFormsContext.Provider, { value: __assign({ dispatch: dispatch }, other) }, children));
};
exports.JsonFormsReduxContext = react_redux_1.connect(function (state) { return (__assign({}, state.jsonforms)); })(JsonFormsReduxProvider);
exports.ctxToArrayLayoutProps = function (ctx, props) {
    return core_1.mapStateToArrayLayoutProps({ jsonforms: __assign({}, ctx) }, props);
};
exports.ctxToArrayControlProps = function (ctx, props) {
    return core_1.mapStateToArrayControlProps({ jsonforms: __assign({}, ctx) }, props);
};
exports.ctxToLayoutProps = function (ctx, props) {
    return core_1.mapStateToLayoutProps({ jsonforms: __assign({}, ctx) }, props);
};
exports.ctxToControlProps = function (ctx, props) {
    return core_1.mapStateToControlProps({ jsonforms: __assign({}, ctx) }, props);
};
exports.ctxToEnumControlProps = function (ctx, props) {
    return core_1.mapStateToEnumControlProps({ jsonforms: __assign({}, ctx) }, props);
};
exports.ctxToControlWithDetailProps = function (ctx, props) {
    return core_1.mapStateToControlWithDetailProps({ jsonforms: __assign({}, ctx) }, props);
};
exports.ctxToAllOfProps = function (ctx, ownProps) {
    var props = core_1.mapStateToAllOfProps({ jsonforms: __assign({}, ctx) }, ownProps);
    return __assign({}, props);
};
exports.ctxDispatchToControlProps = function (dispatch) { return ({
    handleChange: react_1.useCallback(function (path, value) {
        dispatch(core_1.update(path, function () { return value; }));
    }, [dispatch, core_1.update])
}); };
// context mappers
exports.ctxToAnyOfProps = function (ctx, ownProps) {
    var props = core_1.mapStateToAnyOfProps({ jsonforms: __assign({}, ctx) }, ownProps);
    var handleChange = exports.ctxDispatchToControlProps(ctx.dispatch).handleChange;
    return __assign({}, props, { handleChange: handleChange });
};
exports.ctxToOneOfProps = function (ctx, ownProps) {
    var props = core_1.mapStateToOneOfProps({ jsonforms: __assign({}, ctx) }, ownProps);
    var handleChange = exports.ctxDispatchToControlProps(ctx.dispatch).handleChange;
    return __assign({}, props, { handleChange: handleChange });
};
exports.ctxToJsonFormsDispatchProps = function (ctx, ownProps) { return core_1.mapStateToJsonFormsRendererProps({ jsonforms: __assign({}, ctx) }, ownProps); };
exports.ctxDispatchToArrayControlProps = function (dispatch) {
    return core_1.mapDispatchToArrayControlProps(dispatch);
};
exports.ctxToMasterListItemProps = function (ctx, ownProps) { return core_1.mapStateToMasterListItemProps({ jsonforms: __assign({}, ctx) }, ownProps); };
exports.ctxToCellProps = function (ctx, ownProps) {
    return core_1.mapStateToCellProps({ jsonforms: __assign({}, ctx) }, ownProps);
};
exports.ctxToDispatchCellProps = function (ctx, ownProps) {
    return core_1.mapStateToDispatchCellProps({ jsonforms: __assign({}, ctx) }, ownProps);
};
exports.withJsonFormsContext = function (Component) { return function (props) {
    var ctx = exports.useJsonForms();
    return react_1.default.createElement(Component, { ctx: ctx, props: props });
}; };
var withContextToControlProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var controlProps = exports.ctxToControlProps(ctx, props);
        var handleChange = exports.ctxDispatchToControlProps(ctx.dispatch).handleChange;
        return (react_1.default.createElement(Component, __assign({}, props, controlProps, { handleChange: handleChange })));
    };
};
var withContextToLayoutProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var layoutProps = exports.ctxToLayoutProps(ctx, props);
        return (react_1.default.createElement(Component, __assign({}, props, layoutProps)));
    };
};
var withContextToOneOfProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var oneOfProps = exports.ctxToOneOfProps(ctx, props);
        var handleChange = exports.ctxDispatchToControlProps(ctx.dispatch).handleChange;
        return (react_1.default.createElement(Component, __assign({}, props, oneOfProps, { handleChange: handleChange })));
    };
};
var withContextToAnyOfProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var oneOfProps = exports.ctxToAnyOfProps(ctx, props);
        var handleChange = exports.ctxDispatchToControlProps(ctx.dispatch).handleChange;
        return (react_1.default.createElement(Component, __assign({}, props, oneOfProps, { handleChange: handleChange })));
    };
};
var withContextToAllOfProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var allOfProps = exports.ctxToAllOfProps(ctx, props);
        var handleChange = exports.ctxDispatchToControlProps(ctx.dispatch).handleChange;
        return (react_1.default.createElement(Component, __assign({}, props, allOfProps, { handleChange: handleChange })));
    };
};
var withContextToDetailProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var detailProps = exports.ctxToControlWithDetailProps(ctx, props);
        return (react_1.default.createElement(Component, __assign({}, detailProps)));
    };
};
var withContextToArrayLayoutProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var arrayLayoutProps = exports.ctxToArrayLayoutProps(ctx, props);
        var dispatchProps = exports.ctxDispatchToArrayControlProps(ctx.dispatch);
        return (react_1.default.createElement(Component, __assign({}, arrayLayoutProps, dispatchProps)));
    };
};
var withContextToArrayControlProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var stateProps = exports.ctxToArrayControlProps(ctx, props);
        var dispatchProps = exports.ctxDispatchToArrayControlProps(ctx.dispatch);
        return (react_1.default.createElement(Component, __assign({}, props, stateProps, dispatchProps)));
    };
};
var withContextToMasterListItemProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var stateProps = exports.ctxToMasterListItemProps(ctx, props);
        return (react_1.default.createElement(Component, __assign({}, stateProps)));
    };
};
var withContextToCellProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var cellProps = exports.ctxToCellProps(ctx, props);
        var dispatchProps = exports.ctxDispatchToControlProps(ctx.dispatch);
        return (react_1.default.createElement(Component, __assign({}, props, dispatchProps, cellProps)));
    };
};
var withContextToDispatchCellProps = function (Component) { return function (_a) {
    var ctx = _a.ctx, props = _a.props;
    var cellProps = exports.ctxToDispatchCellProps(ctx, props);
    var dispatchProps = exports.ctxDispatchToControlProps(ctx.dispatch);
    return react_1.default.createElement(Component, __assign({}, props, dispatchProps, cellProps));
}; };
var withContextToEnumCellProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var cellProps = exports.ctxToCellProps(ctx, props);
        var dispatchProps = exports.ctxDispatchToControlProps(ctx.dispatch);
        var options = props.options !== undefined ? props.options : props.schema.enum;
        return (react_1.default.createElement(Component, __assign({}, props, dispatchProps, cellProps, { options: options })));
    };
};
var withContextToEnumProps = function (Component) {
    return function (_a) {
        var ctx = _a.ctx, props = _a.props;
        var stateProps = exports.ctxToEnumControlProps(ctx, props);
        var dispatchProps = exports.ctxDispatchToControlProps(ctx.dispatch);
        return (react_1.default.createElement(Component, __assign({}, props, dispatchProps, stateProps, { options: stateProps.options })));
    };
};
exports.areEqual = function (prevProps, nextProps) {
    var prev = omit_1.default(prevProps, ['handleChange', 'renderers', 'cells', 'uischemas']);
    var next = omit_1.default(nextProps, ['handleChange', 'renderers', 'cells', 'uischemas']);
    return isEqual_1.default(prev, next)
        && get_1.default(prevProps, 'renderers.length') === get_1.default(nextProps, 'renderers.length')
        && get_1.default(prevProps, 'cells.length') === get_1.default(nextProps, 'cells.length')
        && get_1.default(prevProps, 'uischemas.length') === get_1.default(nextProps, 'uischemas.length');
};
// top level HOCs --
exports.withJsonFormsControlProps = function (Component) {
    return exports.withJsonFormsContext(withContextToControlProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsLayoutProps = function (Component) {
    return exports.withJsonFormsContext(withContextToLayoutProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsOneOfProps = function (Component) {
    return exports.withJsonFormsContext(withContextToOneOfProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsAnyOfProps = function (Component) {
    return exports.withJsonFormsContext(withContextToAnyOfProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsAllOfProps = function (Component) {
    return exports.withJsonFormsContext(withContextToAllOfProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsDetailProps = function (Component) {
    return exports.withJsonFormsContext(withContextToDetailProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsArrayLayoutProps = function (Component) {
    return exports.withJsonFormsContext(withContextToArrayLayoutProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsArrayControlProps = function (Component) {
    return exports.withJsonFormsContext(withContextToArrayControlProps(react_1.default.memo(Component, function (prevProps, nextProps) { return exports.areEqual(prevProps, nextProps); })));
};
exports.withJsonFormsMasterListItemProps = function (Component) {
    return exports.withJsonFormsContext(withContextToMasterListItemProps(react_1.default.memo(Component, function (prevProps, nextProps) {
        return isEqual_1.default(omit_1.default(prevProps, ['handleSelect', 'removeItem']), omit_1.default(nextProps, ['handleSelect', 'removeItem']));
    })));
};
exports.withJsonFormsCellProps = function (Component) {
    return exports.withJsonFormsContext(withContextToCellProps(react_1.default.memo(Component, function (prevProps, nextProps) { return isEqual_1.default(prevProps, nextProps); })));
};
exports.withJsonFormsDispatchCellProps = function (Component) {
    return exports.withJsonFormsContext(withContextToDispatchCellProps(react_1.default.memo(Component, function (prevProps, nextProps) {
        return isEqual_1.default(prevProps, nextProps);
    })));
};
exports.withJsonFormsEnumCellProps = function (Component) {
    return exports.withJsonFormsContext(withContextToEnumCellProps(react_1.default.memo(Component, function (prevProps, nextProps) { return isEqual_1.default(prevProps, nextProps); })));
};
exports.withJsonFormsEnumProps = function (Component) {
    return exports.withJsonFormsContext(withContextToEnumProps(react_1.default.memo(Component, function (prevProps, nextProps) { return isEqual_1.default(prevProps, nextProps); })));
};
// --
//# sourceMappingURL=JsonFormsContext.js.map